import { Injectable, Optional, Inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpEvent,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap, take, catchError } from 'rxjs/operators';
import { MSAL_INTERCEPTOR_CONFIG, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from '../services/auth.service';

@Injectable()
export class ConditionalMsalInterceptor implements HttpInterceptor {
  private isEnabled = true;

  constructor(
    @Optional() @Inject(MSAL_INTERCEPTOR_CONFIG)
    private msalInterceptorConfig: MsalInterceptorConfiguration | null,
    private msalService: MsalService,
    private authService: AuthService
  ) {
    // Add method to window for controlling interceptor
    (window as any).toggleMsalInterceptor = (enabled: boolean) => {
      this.isEnabled = enabled;
      console.log(`🔧 MSAL Interceptor ${enabled ? 'ENABLED' : 'DISABLED'}`);
    };

    // Add emergency bypass method
    (window as any).emergencyMsalBypass = () => {
      console.log('🚨 Emergency MSAL bypass activated');
      this.isEnabled = false;
      // Also clear authentication state
      this.authService.emergencyAuthReset();
    };
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If interceptor is disabled, just pass through
    if (!this.isEnabled) {
      console.log('🚫 MSAL Interceptor disabled - passing through request:', request.url);
      return next.handle(request);
    }

    // Check if this is a protected resource that needs authentication
    if (this.isProtectedResource(request.url)) {
      console.log('🔐 MSAL Interceptor processing protected resource:', request.url);

      const account = this.msalService.instance.getActiveAccount();

      if (!account) {
        console.warn('⚠️ No active account found for protected resource');
        return next.handle(request);
      }

      const scopes = this.getScopesForResource(request.url);

      if (!scopes.length) {
        console.warn('⚠️ No scopes configured for resource:', request.url);
        return next.handle(request);
      }

      return this.msalService.acquireTokenSilent({
        account,
        scopes,
        prompt: 'none' as any, // Avoid unnecessary interactive prompts
      }).pipe(
        take(1),
        switchMap((result) => {
          console.log('✅ Access token acquired for resource:', request.url);
          const authHeaders = request.headers.set('Authorization', `Bearer ${result.accessToken}`);
          const authRequest = request.clone({ headers: authHeaders });
          return next.handle(authRequest);
        }),
        catchError((error) => {
          // Handle iframe timeout gracefully - don't let it propagate to LoggingInterceptor
          if (error.errorCode === 'monitor_window_timeout' || 
              error.errorCode === 'monitor_window_timeout: Token acquisition in iframe failed due to timeout') {
            console.warn('⏰ Silent token acquisition timed out for:', request.url);
            console.log('💡 This can happen when session is expired or third-party cookies are blocked');
            // Pass the request through without authentication - let the API handle 401 errors
            return next.handle(request);
          }
          
          // For interaction required errors, also pass through
          if (error.errorCode === 'interaction_required' || 
              error.errorMessage?.includes('AADSTS160021') ||
              error.errorMessage?.includes('AADSTS50058')) {
            console.warn('🔄 Interaction required for:', request.url);
            // Pass through and let the API handle auth
            return next.handle(request);
          }
          
          // For other errors, pass through
          console.error('❌ MSAL token acquisition error for:', request.url, error);
          return next.handle(request);
        })
      );
    }

    // Not a protected resource, pass through
    return next.handle(request);
  }

  private isProtectedResource(url: string): boolean {
    if (!this.msalInterceptorConfig?.protectedResourceMap) {
      return false;
    }

    const normalizedUrl = this.normalizeUrl(url);

    for (const resource of this.msalInterceptorConfig.protectedResourceMap.keys()) {
      const normalizedResource = this.normalizeUrl(resource);
      if (
        normalizedUrl.startsWith(normalizedResource) ||
        (normalizedResource.endsWith('/') && normalizedUrl.startsWith(normalizedResource.slice(0, -1)))
      ) {
        return true;
      }
    }

    return false;
  }

  private getScopesForResource(url: string): string[] {
    if (!this.msalInterceptorConfig?.protectedResourceMap) {
      return [];
    }

    const normalizedUrl = this.normalizeUrl(url);

    for (const [resource, scopes] of this.msalInterceptorConfig.protectedResourceMap.entries()) {
      const normalizedResource = this.normalizeUrl(resource);
      if (
        normalizedUrl.startsWith(normalizedResource) ||
        (normalizedResource.endsWith('/') && normalizedUrl.startsWith(normalizedResource.slice(0, -1)))
      ) {
        if (!Array.isArray(scopes)) {
          console.warn('⚠️ Protected resource scopes not configured as array; falling back to empty list.', scopes);
          return [];
        }

        // Handle array of string | ProtectedResourceScopes
        const result: string[] = [];

        for (const scope of scopes) {
          if (typeof scope === 'string') {
            result.push(scope);
          } else if (scope && typeof scope === 'object' && 'scopes' in scope && Array.isArray(scope.scopes)) {
            // Extract scopes from ProtectedResourceScopes object
            result.push(...scope.scopes.filter((s: any) => typeof s === 'string'));
          }
        }

        return result;
      }
    }

    return [];
  }

  private normalizeUrl(url: string): string {
    if (!url) {
      return '';
    }

    return url.trim().replace(/[?#].*$/, '').replace(/\/+/g, '/');
  }
}